import React, { useState } from 'react';
import { Card, Button } from '../ui';
import { Save, FileText, ChevronDown, CheckCircle2 } from 'lucide-react';
export function SettlementConfigView() {
  const [autoGenerate, setAutoGenerate] = useState(true);
  return <div className="space-y-8" data-id="element-453">
      {/* Header */}
      <div className="flex justify-between items-start" data-id="element-454">
        <div data-id="element-455">
          <h2 className="text-3xl font-serif font-bold text-bank-navy" data-id="element-456">
            Settlement Configuration
          </h2>
          <p className="text-slate-500 mt-2 text-lg" data-id="element-457">
            Monitor and configure daily settlement windows and RTGS export
            schedules.
          </p>
        </div>
        <Button variant="primary" className="bg-bank-navy hover:bg-bank-navy-light shadow-sm" data-id="element-458">
          <Save className="mr-2 h-4 w-4" data-id="element-459" />
          Save Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-460">
        {/* Left Column: Daily Settlement Windows */}
        <Card className="h-full" data-id="element-461">
          <div className="px-6 py-5 border-b border-slate-100" data-id="element-462">
            <h3 className="text-xl font-serif font-bold text-bank-navy" data-id="element-463">
              Daily Settlement Windows
            </h3>
            <p className="text-slate-500 mt-1 text-sm" data-id="element-464">
              Monitor and configure daily clearing cycles.
            </p>
          </div>

          <div className="p-6 space-y-8" data-id="element-465">
            {/* Window 1 */}
            <div data-id="element-466">
              <div className="flex justify-between items-center mb-2" data-id="element-467">
                <span className="font-semibold text-bank-navy" data-id="element-468">
                  Window 1 (Morning)
                </span>
                <span className="text-sm font-medium text-green-600" data-id="element-469">
                  Completed
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5 mb-2" data-id="element-470">
                <div className="bg-green-500 h-2.5 rounded-full w-full" data-id="element-471"></div>
              </div>
              <p className="text-xs text-slate-500" data-id="element-472">
                Executed at 10:00 AM • 14,203 txns cleared
              </p>
            </div>

            {/* Window 2 */}
            <div data-id="element-473">
              <div className="flex justify-between items-center mb-2" data-id="element-474">
                <span className="font-semibold text-bank-navy" data-id="element-475">
                  Window 2 (Afternoon)
                </span>
                <span className="text-sm font-medium text-bank-navy" data-id="element-476">
                  In Progress
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5 mb-2" data-id="element-477">
                <div className="bg-bank-navy h-2.5 rounded-full w-[60%]" data-id="element-478"></div>
              </div>
              <p className="text-xs text-slate-500" data-id="element-479">
                Closes at 4:00 PM • 5,102 txns pending
              </p>
            </div>

            <div className="pt-4 border-t border-slate-100" data-id="element-480">
              <h4 className="font-serif font-bold text-bank-navy mb-4" data-id="element-481">
                Export Scheduler
              </h4>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center justify-between" data-id="element-482">
                <div className="flex items-center space-x-3" data-id="element-483">
                  <div className="p-2 bg-white border border-slate-200 rounded text-slate-400" data-id="element-484">
                    <FileText className="h-5 w-5" data-id="element-485" />
                  </div>
                  <div data-id="element-486">
                    <p className="font-semibold text-bank-navy text-sm" data-id="element-487">
                      RTGS Settlement File
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-488">
                      Auto-generate every 12 hours
                    </p>
                  </div>
                </div>

                {/* Toggle Switch */}
                <button onClick={() => setAutoGenerate(!autoGenerate)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-bank-navy focus:ring-offset-2 ${autoGenerate ? 'bg-green-500' : 'bg-slate-200'}`} data-id="element-489">
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoGenerate ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-490" />
                </button>
              </div>
            </div>
          </div>
        </Card>

        {/* Right Column: Settlement Schedule */}
        <Card className="h-full" data-id="element-491">
          <div className="px-6 py-5 border-b border-slate-100" data-id="element-492">
            <h3 className="text-xl font-serif font-bold text-bank-navy" data-id="element-493">
              Settlement Schedule
            </h3>
            <p className="text-slate-500 mt-1 text-sm" data-id="element-494">
              Configure timing and frequency of settlement cycles.
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-495">
            <div className="space-y-4" data-id="element-496">
              <div data-id="element-497">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-498">
                  Window 1 Start Time
                </label>
                <div className="relative" data-id="element-499">
                  <input type="text" defaultValue="10:00 AM" className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-bank-navy focus:outline-none focus:ring-1 focus:ring-bank-navy" data-id="element-500" />
                </div>
              </div>
              <div data-id="element-501">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-502">
                  Window 2 Start Time
                </label>
                <div className="relative" data-id="element-503">
                  <input type="text" defaultValue="04:00 PM" className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-bank-navy focus:outline-none focus:ring-1 focus:ring-bank-navy" data-id="element-504" />
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100" data-id="element-505">
              <h4 className="font-serif font-bold text-bank-navy mb-4" data-id="element-506">
                RTGS Export Settings
              </h4>
              <div className="space-y-4" data-id="element-507">
                <div className="flex items-center justify-between" data-id="element-508">
                  <label className="text-sm font-medium text-slate-700" data-id="element-509">
                    Export Frequency
                  </label>
                  <div className="relative w-48" data-id="element-510">
                    <select className="block w-full appearance-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm pr-8 focus:border-bank-navy focus:outline-none focus:ring-1 focus:ring-bank-navy" data-id="element-511">
                      <option data-id="element-512">Every 12 hours</option>
                      <option data-id="element-513">Every 24 hours</option>
                      <option data-id="element-514">Real-time</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-515" />
                  </div>
                </div>
                <div className="flex items-center justify-between" data-id="element-516">
                  <label className="text-sm font-medium text-slate-700" data-id="element-517">
                    File Format
                  </label>
                  <div className="relative w-48" data-id="element-518">
                    <select className="block w-full appearance-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm pr-8 focus:border-bank-navy focus:outline-none focus:ring-1 focus:ring-bank-navy" data-id="element-519">
                      <option data-id="element-520">ISO 20022 (XML)</option>
                      <option data-id="element-521">CSV</option>
                      <option data-id="element-522">JSON</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-523" />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4" data-id="element-524">
              <Button variant="outline" className="w-full justify-center border-slate-300 text-bank-navy hover:bg-slate-50" data-id="element-525">
                Generate Manual Export Now
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Bottom Card: Settlement History */}
      <Card data-id="element-526">
        <div className="px-6 py-5 border-b border-slate-100" data-id="element-527">
          <h3 className="text-xl font-serif font-bold text-bank-navy" data-id="element-528">
            Settlement History
          </h3>
          <p className="text-slate-500 mt-1 text-sm" data-id="element-529">
            Recent settlement cycle completions.
          </p>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-530">
          {[{
          window: 'Window 1 - Morning Cycle',
          date: 'Feb 16, 2026 at 10:00 AM',
          status: 'Completed',
          txns: '14,203 transactions'
        }, {
          window: 'Window 2 - Afternoon Cycle',
          date: 'Feb 15, 2026 at 04:00 PM',
          status: 'Completed',
          txns: '8,942 transactions'
        }, {
          window: 'Window 1 - Morning Cycle',
          date: 'Feb 15, 2026 at 10:00 AM',
          status: 'Completed',
          txns: '12,156 transactions'
        }, {
          window: 'Window 2 - Afternoon Cycle',
          date: 'Feb 14, 2026 at 04:00 PM',
          status: 'Completed',
          txns: '7,890 transactions'
        }].map((item, idx) => <div key={idx} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-531">
              <div data-id="element-532">
                <p className="font-medium text-bank-navy text-sm" data-id="element-533">
                  {item.window}
                </p>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-534">{item.date}</p>
              </div>
              <div className="text-right" data-id="element-535">
                <p className="text-sm font-medium text-green-600" data-id="element-536">
                  {item.status}
                </p>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-537">{item.txns}</p>
              </div>
            </div>)}
        </div>
      </Card>
    </div>;
}